import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsBoxArrowUpRight, BsCheck, BsFileEarmarkPdf } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button, Flex, HStack, Icon, Link, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../../../../../../../../../../../components/Spinner';
import { analyticsIds } from '../../../../../../../../../../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../../../../../../../../../../constants/cypressIds';
import { hideModal } from '../../../../../../../../../../../../../../../redux/modules/modal';
import { useAllCompanyPolicyTypesData } from '../../../../../../../../../../../../../../../services/company-service';
import { useConfirmExtractedExpirations, useCreateDocumentExpirations } from '../../../../../../../../../../../../../../../services/document-expiration-service';
import { useGptRequest } from '../../../../../../../../../../../../../../../services/gpt-request-service';
import colors from '../../../../../../../../../../../../../../../styles/theme/foundation/colors';
import { getDateString } from '../../../../../../../../../../../../../../../utils/dateUtil';
import ExpirationRowsEditor from '../../ExpirationRowsEditor';
import { createRow, rowToPayload, soonestDate } from '../../ExpirationRowsEditor/util';

import { buildRowsFromResult, findResultForDocument } from './reviewExtractedDatesUtil';

const ReviewExtractedDatesModal = ({
  gptRequestId = null,
  documentId,
  recordId,
  insuredId,
  file
}) => {
  const isManualEntry = !gptRequestId;
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const [rows, setRows] = useState([]);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const seededRef = useRef(false);

  const { data: gptRequest, isLoading } = useGptRequest({ gptRequestId });

  // confirming an extraction goes through its own route so superseded links on older documents are
  // retired; manual entry is an ordinary create and must not trigger that
  const confirmMutation = useConfirmExtractedExpirations({ gptRequestId, docId: documentId, recordId, insuredId });
  const createMutation = useCreateDocumentExpirations({ docId: documentId, recordId, insuredId });
  const { mutateAsync: createExpirations, isLoading: isSaving } = isManualEntry ? createMutation : confirmMutation;
  const { data: policyTypes = [], isFetched: policyTypesFetched } = useAllCompanyPolicyTypesData(companyId, { enabled: !!companyId });

  const fileUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => () => URL.revokeObjectURL(fileUrl), [fileUrl]);

  // seed editable state once, after the policy types needed for pre-linking have loaded;
  // later refetches must not clobber the user's edits
  useEffect(() => {
    if (seededRef.current) return;
    if (!isManualEntry && !gptRequest) return;
    if (companyId && !policyTypesFetched) return;

    seededRef.current = true;
    const seeded = isManualEntry ? [] : buildRowsFromResult(findResultForDocument(gptRequest, documentId), policyTypes);
    setRows(seeded.length ? seeded : [createRow({ isExpanded: true })]);
  }, [gptRequest, isManualEntry, policyTypesFetched]);

  const soonest = useMemo(() => soonestDate(rows), [rows]);
  const hasIncompleteRow = rows.some(row => !row.date);

  const handleConfirm = async () => {
    try {
      await createExpirations(rows.map(rowToPayload));
    } catch (error) {
      return;
    }

    dispatch(hideModal());
  };

  if (isLoading || (!seededRef.current && !isManualEntry)) {
    return <LoadingSpinner isInline minHeight="200px" />;
  }

  return (
    <Box textAlign="left">
      <Text
        color={colors.greyScale['900']}
        fontSize="22px"
        fontWeight="semibold"
        lineHeight="30px"
        mb={2}
      >
        {isManualEntry ? 'Enter expiration dates' : 'Review the dates found'}
      </Text>

      <Text color={colors.greyScale['700']} fontSize="17px" lineHeight="26px">
        {isManualEntry ? 'Add a label and date for each expiration on this document.' : 'Confirm the label(s) and date(s) below.'}
        {' '}
        {soonest ? (
          <>
            The document will show
            {' '}
            <Text as="span" color={colors.greyScale['900']} fontWeight="semibold">
              {getDateString(soonest, 'MM/DD/YYYY')}
            </Text>
            {' '}
            &mdash; the soonest to expire date.
          </>
        ) : (
          'The document will show no expiration date until one is added.'
        )}
      </Text>

      <Link
        isExternal
        alignItems="center"
        color={colors.primaryScale['700']}
        display="inline-flex"
        fontSize="17px"
        fontWeight="semibold"
        gap={2}
        href={fileUrl}
        mb={8}
        mt={3}
      >
        <Icon as={BsFileEarmarkPdf} boxSize="18px" />
        {file.name}
        <Icon as={BsBoxArrowUpRight} boxSize="14px" />
      </Link>

      <ExpirationRowsEditor
        heading={isManualEntry ? 'Dates' : 'Dates found'}
        policyTypes={policyTypes}
        rows={rows}
        setRows={setRows}
      />

      {isConfirmingCancel ? (
        <Flex alignItems="center" flexWrap="wrap" gap={4} justifyContent="space-between" mt={8}>
          <Text color={colors.greyScale['800']} flex="1 1 240px">
            Discard these dates? The document stays uploaded without an expiration date.
          </Text>

          <HStack spacing={3}>
            <Button size="lg" variant="secondary" onClick={() => setIsConfirmingCancel(false)}>
              Keep Editing
            </Button>
            <Button size="lg" variant="secondaryError" onClick={() => dispatch(hideModal())}>
              Yes, Discard
            </Button>
          </HStack>
        </Flex>
      ) : (
        <HStack justifyContent="flex-end" mt={8} spacing={4}>
          <Button
            data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.expirationExtraction.cancelReviewButton}
            data-cy={CypressIds.expirationReviewCancelButton}
            size="lg"
            variant="secondary"
            onClick={() => setIsConfirmingCancel(true)}
          >
            Cancel
          </Button>

          <Button
            data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.expirationExtraction.confirmUploadButton}
            data-cy={CypressIds.expirationReviewConfirmUploadButton}
            isDisabled={rows.length === 0 || hasIncompleteRow}
            isLoading={isSaving}
            leftIcon={<Icon as={BsCheck} boxSize="20px" />}
            size="lg"
            variant="primary"
            onClick={handleConfirm}
          >
            Confirm &amp; Upload
          </Button>
        </HStack>
      )}
    </Box>
  );
};

ReviewExtractedDatesModal.propTypes = {
  gptRequestId: PropTypes.number,
  documentId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  file: PropTypes.object.isRequired
};

export default ReviewExtractedDatesModal;
